import { get, post, update } from '@apis/api';
import {
  CouponAggsResponse,
  CouponDownloadApiResponse,
  CouponFilterNameResponse,
  CouponInfo,
} from '@models/coupon';
import {
  BrandAggsResponse,
  BrandFilterNameResponse,
  BrandProductResponse,
} from '@models/brand';
import type { RequestBrandProductListParams } from '@apis/renewal/brand';
import { PageCommonResponse } from '@wconcept/wconcept-common/models/display/common';
import { CouponDownloadApiReq } from '@wconcept/wconcept-common/models/event/coupon-download-api-req';
import { CommonResponse } from '@wconcept/wconcept-common/models/common-response';
import { IAlarmRes } from './event';

export interface CouponDownloadResponse extends PageCommonResponse {
  data: CouponDownloadApiResponse;
}

export interface ICouponDownlodInfos {
  couponDownlodInfos: ICouponDownlodInfo[];
  resultCode: number;
  resultMessage: string;
}

export interface ICouponDownlodInfo {
  couponDownlodInfos: ICouponDownlodInfo[];
  resultCode: number;
  resultMessage: string;
}

const baseURL = process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_URL;
const options = {
  headers: {
    'Content-Type': 'application/json; charset=utf-8', // 필요에 따라 헤더 수정
    'DISPLAY-API-KEY': process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_KEY || '',
  },
};

const eventOptions = {
  headers: {
    'Content-Type': 'application/json; charset=utf-8', // 필요에 따라 헤더 수정
    'EVENT-API-KEY': process.env.NEXT_PUBLIC_EVENT_API_KEY || '',
  },
};

export const requestCouponDownloadAll = async ({
  couponCds,
}: {
  couponCds: string[];
}) => {
  const { data } = await post<CouponDownloadResponse>({
    url: `/display/api/coupon/v1/download-all`,
    data: {
      couponCds,
    },
    options,
    baseUrl: baseURL,
  });
  return data;
};

export const requestCouponDownload = async (params: CouponDownloadApiReq) => {
  const { data } = await post<CommonResponse<ICouponDownlodInfos>>({
    url: `/event/api/v1/coupon/download`,
    baseUrl: baseURL,
    options: eventOptions,
    data: params,
  });
  return data;
};

/**
 * 마케팅 수신 동의 (회원 정보 갱신)
 *  - PATCH /event/api/v1/customer/receive-agree
 *  - Request body 없음, 호출만으로 백엔드가 동의 처리
 *  - 응답의 data 는 항상 null, 성공/실패 여부는 result, message 로 판단하므로
 *    응답 전체(CommonResponse)를 그대로 반환
 */
export const requestUpdateReceiveAgree = async () => {
  const res = await update<CommonResponse<null>>({
    url: `/event/api/v1/customer/receive-agree`,
    baseUrl: baseURL,
    options: eventOptions,
    data: {},
  });
  return res;
};

export const requestGetEventAlarm = async (
  alarmId: number,
  custNo?: string
) => {
  const { data } = await get<
    CommonResponse<IAlarmRes>,
    {
      custNo: number;
      eventId: number;
    }
  >({
    url: `/event/api/v1/alarm/${alarmId}?custNo=${custNo}`,
    baseUrl: baseURL,
    options: eventOptions,
  });
  return data;
};

export interface CouponInfoResponse extends PageCommonResponse {
  data: CouponInfo;
}

export const requestGetCouponInfo = async ({
  couponCd,
}: {
  couponCd: string;
}) => {
  const { data } = await get<CouponInfoResponse>({
    url: `/display/api/coupon/v1/coupon/${couponCd}`,
    options,
    baseUrl: baseURL,
  });
  return data;
};

export interface RequestCouponProductListParams {
  custNo: string;
  gender: string;
  sort?: string;
  pageNo?: number;
  pageSize?: number;
  shopCds?: string[];
  bcds?: string[];
  mcd?: string;
  ccds?: string[];
  pcds?: string[];
  lcds?: string[];
  source?: string;
  device?: string;
  searchType?: string;
  priceSections?: number[];
  minPrice?: number;
  maxPrice?: number;
  colors?: string[];
  benefits?: string[];
  discounts?: string[];
  status?: string[];
  saleTags?: string[];
  keyword?: string;
}

export const requestCouponResultProductInit = async (
  couponCd: string,
  params: RequestCouponProductListParams
) => {
  const {
    custNo,
    gender,
    sort,
    pageNo = 1,
    pageSize,
    shopCds,
    mcd,
    ccds,
    bcds,
    pcds,
    lcds,
    minPrice,
    maxPrice,
    colors,
    benefits,
    discounts,
    status,
    saleTags,
    keyword,
  } = params;
  const { data } = await post<CouponFilterNameResponse>({
    url: `/display/api/coupon/v1/coupon/${couponCd}/products/init`,
    options,
    baseUrl: baseURL,
    data: {
      custNo,
      gender,
      sort,
      pageNo,
      pageSize,
      shopCds,
      mcd,
      ccds,
      bcds,
      pcds,
      lcds,
      minPrice,
      maxPrice,
      colors,
      benefits,
      discounts,
      status,
      saleTags,
      keyword,
    },
  });

  return data;
};

export const requestCouponProductResult = async (
  couponCd: string,
  params: RequestCouponProductListParams
) => {
  const {
    custNo,
    gender,
    sort,
    pageNo = 1,
    pageSize,
    bcds,
    mcd,
    ccds,
    pcds,
    lcds,
    priceSections,
    minPrice,
    maxPrice,
    colors,
    benefits,
    discounts,
    status,
    saleTags,
    keyword,
  } = params;
  const { data } = await post<BrandProductResponse>({
    url: `/display/api/coupon/v1/coupon/${couponCd}/products`,
    options,
    baseUrl: baseURL,
    data: {
      custNo,
      gender,
      sort,
      pageNo,
      pageSize,
      bcds,
      mcd,
      ccds,
      pcds,
      lcds,
      priceSections,
      minPrice,
      maxPrice,
      colors,
      benefits,
      discounts,
      status,
      saleTags,
      keyword,
    },
  });
  return data;
};

export const requestCouponProductAggByType = async (
  couponCd: string,
  type: string,
  requestBody: Record<string, unknown>
) => {
  const { data } = await post<CouponAggsResponse>({
    url: `/display/api/coupon/v1/coupon/${couponCd}/products/aggs/${type}`,
    baseUrl: baseURL,
    options,
    data: requestBody,
  });
  return data;
};
